import React, { useContext, useState, useEffect, FunctionComponent, useMemo, ReactElement, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Ui
import ModelTable from '@/ui/Table/ModelTable';
import TableButtonView from '@/ui/Table/ActionButtons/TableButtonView';
import TableButtonEdit from '@/ui/Table/ActionButtons/TableButtonEdit';
import TableButtonShowPhotos from '@/ui/Table/ActionButtons/TableButtonShowPhotos';
import TableButtonDocuments from '@/ui/Table/ActionButtons/TableButtonDocuments';
import { DialogFormGenerateFunction } from '@/ui/MapCentric/MapCentricPage';

// Components
import useDocuments from '@/components/useDocuments';

// Context
import DialogContext, { DialogContextType, IDialogProps } from '@/context/DialogContext/DialogContext';
import LoaderContext, { LoaderContextType } from '@/context/LoaderContext/LoaderContext';
import SnackbarContext, { SnackbarContextType } from '@/context/SnackbarContext/SnackbarContext';

// Lib
import DataController, { IDataController } from '@/lib/DataController';

// Types
import { DCRecord } from '@/@types/lib/dataController';
import { IModel } from '@/@types/models/model';
import { ClosingDetails } from '@/@types/components/formController';
import { AllowRowActionFn, RowActionFn } from '@/@types/ui/Table';
import TableHeaderButtonShowDeleted from '../Table/HeaderButtons/TableHeaderButtonShowDeleted';

interface IMapCentricPrimaryTableProps {
  onLoad?: (records: Array<DCRecord>) => void;
  onRecordSelect: (id: number) => void;
  onRowAction?: RowActionFn;
  onAllowRowAction?: AllowRowActionFn;
  setAllowImages: () => void;

  imagesEnabled: boolean;

  selectedRecordId?: number;
  view: string | undefined;

  model: IModel;
  titleToken: string;

  allowAdd?: boolean;
  allowView?: boolean;
  allowEdit?: boolean;
  allowExport?: boolean;
  allowPhotos?: boolean;
  allowExportShape?: boolean;
  allowShowDeleted?: boolean;

  dialogComponent?: React.FunctionComponent<IDialogProps>;
  dialogForm?: string | DialogFormGenerateFunction;
  dialogAsPagePath?: string | undefined;
  dialogTitle?: string;

  multiSelect?: boolean;
  selectedIds?: number[];
  onSelectionChange?: (ids: number[]) => void;
  // footer buttons acting on the selection, returned as an array so the table can inject `selection` into each
  renderSelectionActions?: (refresh: () => void) => ReactElement[];
  headerControl?: ReactNode;
}

const MapCentricPrimaryTable: FunctionComponent<React.PropsWithChildren<IMapCentricPrimaryTableProps>> = (props) => {
  const { showDialog } = useContext<DialogContextType>(DialogContext);
  const loaderContext = useContext(LoaderContext) as LoaderContextType;
  const snackbarContext = useContext(SnackbarContext) as SnackbarContextType;
  const { checkIfHasGid, showDocumentsDialog } = useDocuments();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const { onLoad, onRowAction, setAllowImages, onRecordSelect } = props;
  const { model, titleToken, view, imagesEnabled, selectedRecordId, allowShowDeleted, onAllowRowAction } = props;
  const { allowAdd, allowView, allowEdit, allowPhotos, allowExport, allowExportShape } = props;
  const { dialogComponent, dialogForm, dialogAsPagePath, dialogTitle } = props;
  const { multiSelect, selectedIds, onSelectionChange, renderSelectionActions, headerControl } = props;

  const [records, setRecords] = useState<DCRecord[]>([]);
  const [showDeleted, setShowDeleted] = useState(false);

  const filteredRecords = useMemo(() => {
    if (!allowShowDeleted) return records;
    if (showDeleted) return records;
    return records.filter((rec) => !rec.is_deleted);
  }, [records, showDeleted]);

  const deletedHeaderButton = allowShowDeleted ? (
    <TableHeaderButtonShowDeleted showDeleted={showDeleted} onClick={() => setShowDeleted((prev) => !prev)} />
  ) : null;

  useEffect(() => {
    refreshRecords();
  }, []);

  const refreshRecords = () => {
    loaderContext.toggleLoading(true);
    dc.GetData()
      .then((resp) => {
        if (resp.success && Array.isArray(resp.data)) {
          setRecords(resp.data);
          if (onLoad) onLoad(resp.data);
          if (resp.data.length === 0) {
            snackbarContext.showNotification(t('messages.nodata'), 'warning');
          }
        }
      })
      .finally(() => {
        loaderContext.toggleLoading(false);
      });
  };

  const dc: IDataController = new DataController(model);

  const handleView = (evt: React.SyntheticEvent, sel: { [key: string]: DCRecord }) => {
    const selId = parseInt(Object.keys(sel)[0], 10);
    const record: DCRecord = sel[selId];

    const form = typeof dialogForm === 'function' ? dialogForm(record) : dialogForm;

    if (dialogComponent && record?.id && dialogAsPagePath === undefined) {
      showDialog(dialogComponent, {
        title: dialogTitle,
        dc,
        mode: 'view',
        form,
        recordId: record.id,
        onClose: handleDialogClose,
      });
    } else if (record !== undefined && record?.id && dialogAsPagePath !== undefined) {
      navigate(dialogAsPagePath + String(record.id), {
        state: { view: 'view', form },
      });
    }
  };

  const handleEdit = (evt: React.SyntheticEvent, sel: { [key: string]: DCRecord }) => {
    const selId = parseInt(Object.keys(sel)[0], 10);
    const record: DCRecord = sel[selId];

    const form = typeof dialogForm === 'function' ? dialogForm(record) : dialogForm;

    if (dialogComponent && record?.id && dialogAsPagePath === undefined) {
      showDialog(dialogComponent, {
        title: dialogTitle,
        dc,
        mode: 'update',
        form: 'update',
        initialRecord: record,
        onClose: handleDialogClose,
      });
    } else if (record !== undefined && record?.id && dialogAsPagePath !== undefined) {
      navigate(dialogAsPagePath + String(record.id), {
        state: { view: 'update', form },
      });
    }
  };

  const handleDocuments = (evt: React.SyntheticEvent, sel: { [key: string]: DCRecord }) => {
    const selId = parseInt(Object.keys(sel)[0], 10);
    const record: DCRecord = sel[selId];
    const gid = record.gid as string;

    if (gid) {
      showDocumentsDialog(gid, handleDialogClose);
    }
  };

  const isDocumentsButtonEnabled: (selection: { [key: string]: DCRecord }) => boolean = (sel) => {
    const selId = parseInt(Object.keys(sel)[0], 10);
    const record: DCRecord = sel[selId];

    if (allowShowDeleted) {
      return !!record.gid && !record.is_deleted;
    }

    return !!record.gid;
  };

  const handleAdd = () => {
    if (dialogComponent) {
      showDialog(dialogComponent, {
        title: dialogTitle,
        dc,
        mode: 'insert',
        form: 'insert',
        onClose: handleDialogClose,
      });
    }
  };

  const handleDialogClose = (result: ClosingDetails) => {
    if (result.dataChanged) {
      refreshRecords();
    }
  };

  const hasGid = checkIfHasGid(records);

  return (
    <ModelTable
      title={t(titleToken)}
      records={filteredRecords}
      dc={dc}
      allowSelection={multiSelect ? 'many' : 'one'}
      allowColumnPicker
      allowFilter
      allowExport={allowExport}
      allowExportShape={allowExportShape}
      allowAdd={allowAdd}
      handleAdd={handleAdd}
      addLabel={`${t('buttons.new_f')}`}
      onRowAction={onRowAction || (() => {})}
      onRowClick={
        multiSelect
          ? undefined
          : (evt: React.SyntheticEvent, sel: { [key: string]: DCRecord }) => {
              const clickedRecord = sel[Object.keys(sel)[0]];
              return onRecordSelect(clickedRecord.id as number);
            }
      }
      allowRowAction={onAllowRowAction || (() => true)}
      viewName={view}
      rowSelectedCustom={multiSelect ? undefined : selectedRecordId}
      selectedIds={multiSelect ? selectedIds || [] : undefined}
      onSelectionChange={multiSelect ? onSelectionChange : undefined}
      customHeaderControl={
        headerControl ? (
          <>
            {headerControl}
            {deletedHeaderButton}
          </>
        ) : (
          deletedHeaderButton
        )
      }
    >
      {hasGid && <TableButtonDocuments isEnabledRecord={isDocumentsButtonEnabled} onClick={handleDocuments} />}
      {allowPhotos && <TableButtonShowPhotos disabled={!imagesEnabled} variant="outlined" onClick={setAllowImages} />}
      {allowEdit && <TableButtonEdit variant="outlined" onClick={handleEdit} />}
      {allowView && <TableButtonView variant="contained" onClick={handleView} />}
      {renderSelectionActions ? renderSelectionActions(refreshRecords) : null}
    </ModelTable>
  );
};

export default MapCentricPrimaryTable;
